import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { UserPlus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { branchListQueryOptions } from '@/api/reference-api'
import type { CustomerSummary } from '@/api/types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { ArrearsIndicator } from '@/features/customers/arrears-indicator'
import { customerStatusLabel, kycStatusLabel } from '@/features/customers/constants'
import { CustomerFilters } from '@/features/customers/customer-filters'
import { useCustomerPermissions } from '@/features/customers/permissions'
import { useCustomerList } from '@/features/customers/queries'
import { useRowNavigation } from '@/features/customers/use-row-navigation'

import { DEFAULT_PAGE_SIZE, type CustomerListSearch } from './search'

/**
 * The customer list.
 *
 * This is the screen a teller and an account officer sit on all day, so it is built for the
 * keyboard: the caret starts in the search box, Arrow Down walks into the results, Enter opens
 * one. Everything that narrows the list lives in the URL, which is what makes a filtered view
 * shareable and the Back button honest.
 */

/**
 * Nothing here is sortable. The list endpoint takes no ordering parameter, and ordering the page
 * that happens to be in the browser would be a claim about the whole result set that the next page
 * would immediately contradict.
 */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<CustomerSummary>()

const columns: DataTableColumn<CustomerSummary>[] = helper.columns([
  helper.accessor('customerNumber', {
    header: 'Customer no.',
    enableSorting: false,
    cell: (context) => (
      <span className="font-mono text-xs">{context.row.original.customerNumber}</span>
    ),
    // On a phone the customer number sits under the name rather than above it: a person is
    // identified to a caller by their name, and the number is what confirms it.
    meta: { noWrap: true, widthClass: 'w-40', card: 'lead' },
  }),
  helper.accessor('fullName', {
    header: 'Name',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{context.row.original.fullName}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('phoneNumber', {
    header: 'Phone',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.phoneNumber}</span>,
    meta: { noWrap: true, widthClass: 'w-40' },
  }),
  helper.accessor('branchName', {
    header: 'Branch',
    enableSorting: false,
    // The identifier when there is no name, because no module owns branches as records yet. An
    // empty cell here reads as "no branch", and every customer has one.
    cell: (context) =>
      context.row.original.branchName ?? `Branch ${context.row.original.branchId}`,
    meta: { noWrap: true },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    // The badge says "Active" or "Blacklisted" in words, so it needs no label beside the name.
    meta: { widthClass: 'w-32', card: 'lead' },
  }),
  helper.accessor('kycStatus', {
    header: 'KYC',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.kycStatus} />,
    meta: { widthClass: 'w-36' },
  }),
  helper.accessor('hasArrearsAnywhere', {
    header: 'Arrears',
    enableSorting: false,
    cell: (context) => <ArrearsIndicator inArrears={context.row.original.hasArrearsAnywhere} />,
    meta: { widthClass: 'w-32' },
  }),
])

/** Long enough that a name typed at speed is one request, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 300

export function CustomerListPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/authenticated/customers' })
  const permissions = useCustomerPermissions()

  const branches = useQuery(branchListQueryOptions())
  const [searchText, setSearchText] = useState(search.search ?? '')

  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE

  /**
   * Every change to the list's state goes through here, spelled out member by member rather than
   * spread from whatever the previous route carried. A filter left behind by another screen is
   * exactly the kind of thing that makes a list quietly show the wrong rows.
   */
  const updateSearch = (patch: Partial<CustomerListSearch>, replace = false) => {
    void navigate({
      to: '/customers',
      search: {
        search: search.search,
        status: search.status,
        kycStatus: search.kycStatus,
        branchId: search.branchId,
        page: search.page,
        pageSize: search.pageSize,
        ...patch,
      },
      replace,
    })
  }

  // What this screen last wrote into the URL. Comparing against it is how a Back button, or a
  // pasted link, gets mirrored into the box without the debounce writing it straight back out.
  const lastPushed = useRef(search.search ?? '')

  useEffect(() => {
    const fromUrl = search.search ?? ''
    if (fromUrl !== lastPushed.current) {
      lastPushed.current = fromUrl
      setSearchText(fromUrl)
    }
  }, [search.search])

  useEffect(() => {
    const trimmed = searchText.trim()
    if (trimmed === (search.search ?? '')) {
      return
    }

    const timer = window.setTimeout(() => {
      lastPushed.current = trimmed
      void navigate({
        to: '/customers',
        search: {
          search: trimmed === '' ? undefined : trimmed,
          status: search.status,
          kycStatus: search.kycStatus,
          branchId: search.branchId,
          page: undefined,
          pageSize: search.pageSize,
        },
        // Typing produces one history entry for the search, not one per keystroke.
        replace: true,
      })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
    // Every filter is a dependency, not only the search text: changing one mid-keystroke restarts
    // the debounce against the new values. A timer left holding the filters as they were would
    // land 300ms later and quietly undo the change the operator just made.
  }, [
    searchText,
    search.search,
    search.status,
    search.kycStatus,
    search.branchId,
    search.pageSize,
    navigate,
  ])

  const query = useCustomerList({
    search: search.search,
    status: search.status,
    kycStatus: search.kycStatus,
    branchId: search.branchId,
    page,
    pageSize,
  })

  const rows = query.data?.items ?? []

  const openCustomer = (customer: CustomerSummary) => {
    void navigate({ to: '/customers/$customerId', params: { customerId: customer.id } })
  }

  const rowNavigation = useRowNavigation({
    rowCount: rows.length,
    onActivate: (index) => {
      const customer = rows[index]
      if (customer) {
        openCustomer(customer)
      }
    },
  })

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater

    updateSearch({ page: next.pageIndex + 1, pageSize: next.pageSize })
  }

  const hasActiveFilters = Boolean(
    search.search ?? search.status ?? search.kycStatus ?? search.branchId,
  )

  const clearFilters = () => {
    lastPushed.current = ''
    setSearchText('')
    void navigate({ to: '/customers', search: { pageSize: search.pageSize } })
  }

  const describeFilters = () => {
    const parts: string[] = []
    if (search.search) {
      parts.push(`matching “${search.search}”`)
    }
    if (search.status) {
      parts.push(`with status ${customerStatusLabel(search.status).toLowerCase()}`)
    }
    if (search.kycStatus) {
      parts.push(`whose KYC is ${kycStatusLabel(search.kycStatus).toLowerCase()}`)
    }
    if (search.branchId) {
      const branch = branches.data?.find((candidate) => candidate.id === search.branchId)
      parts.push(`at ${branch?.name ?? 'the selected branch'}`)
    }

    return parts.join(', ')
  }

  return (
    <>
      <PageHeader
        title="Customers"
        description="One person, one Ghana Card, one file — whichever branch they walk into. Identity is global; the accounts hanging off it are not."
        actions={
          permissions.canCreate ? (
            <Button asChild variant="primary">
              <Link to="/customers/new">
                <UserPlus />
                New customer
              </Link>
            </Button>
          ) : null
        }
      />

      <div
        ref={rowNavigation.containerRef}
        onKeyDown={rowNavigation.onKeyDown}
        onClick={rowNavigation.onClick}
        className="space-y-3"
      >
        <CustomerFilters
          searchText={searchText}
          onSearchTextChange={setSearchText}
          status={search.status}
          onStatusChange={(status) => {
            updateSearch({ status, page: undefined })
          }}
          kycStatus={search.kycStatus}
          onKycStatusChange={(kycStatus) => {
            updateSearch({ kycStatus, page: undefined })
          }}
          branchId={search.branchId}
          onBranchChange={(branchId) => {
            updateSearch({ branchId, page: undefined })
          }}
          branches={branches.data ?? []}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />

        <DataTable
          columns={columns}
          data={query.data?.items}
          rowCount={query.data?.totalCount}
          pagination={{ pageIndex: page - 1, pageSize }}
          onPaginationChange={onPaginationChange}
          sorting={UNSORTED}
          onSortingChange={ignoreSortingChange}
          isLoading={query.isPending}
          isFetching={query.isFetching}
          error={query.isError ? query.error : null}
          onRetry={() => void query.refetch()}
          getRowId={(row) => row.id}
          onRowActivate={openCustomer}
          pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100, 200]}
          emptyTitle={hasActiveFilters ? 'No customers match' : 'No customers yet'}
          emptyDescription={
            hasActiveFilters
              ? `Nothing was found ${describeFilters()}. A phone number has to be typed in full to match.`
              : 'Nobody has been onboarded at the branches you can see. The first record starts with a Ghana Card.'
          }
          emptyAction={
            hasActiveFilters ? (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear the filters
              </Button>
            ) : permissions.canCreate ? (
              <Button asChild variant="primary" size="sm">
                <Link to="/customers/new">
                  <UserPlus />
                  New customer
                </Link>
              </Button>
            ) : null
          }
        />
      </div>
    </>
  )
}
